/**
 * Pricing Freshness ADR — Backend Confirmation record (Phase 2 foundation).
 *
 * Evidence only: the backend confirmed pricing for input epoch E at time T.
 * Not proof of ongoing correctness. Not created by transport-memo hits alone.
 */

export type PricingConfirmation = {
  /**
   * Input-epoch identity. Reuses the committed (debounced) pricing fingerprint —
   * the same identity that drives `pricingPreviewQueryKey`.
   */
  epoch: string
  /** Wall-clock ms when Preview Billing HTTP succeeded for this epoch. */
  confirmedAt: number
  /** Transport memo key for the successful Preview (response / request identity). */
  queryKey: string
  /** Lightweight response identity from the confirmed payload. */
  payableAmount: number
}

type StageInput = {
  epochAtStart: string
  queryKeyAtStart: string
  /** Live committed epoch after Preview returns (may have moved). */
  currentEpoch: string
  currentQueryKey: string
  confirmedAt: number
  payableAmount: number
}

/**
 * After a successful `previewOrder` HTTP response, stage a confirmation only if
 * the committed epoch/key are still current (drops aborted / superseded work).
 * Returns null when the response must not become Pricing Confirmation.
 */
export function stagePricingConfirmationAfterPreviewSuccess(
  input: StageInput
): PricingConfirmation | null {
  if (!input.epochAtStart || !input.queryKeyAtStart) return null
  if (input.epochAtStart !== input.currentEpoch) return null
  if (input.queryKeyAtStart !== input.currentQueryKey) return null
  return {
    epoch: input.epochAtStart,
    confirmedAt: input.confirmedAt,
    queryKey: input.queryKeyAtStart,
    payableAmount: input.payableAmount,
  }
}

type CommitInput = {
  pending: PricingConfirmation | null
  /** Network settle: safe-query not loading. */
  networkIdle: boolean
  hasDisplayPayload: boolean
  currentEpoch: string
  currentQueryKey: string
}

/**
 * Promote a staged confirmation into the owned record once Display has payload
 * and Network is idle for the matching epoch. Returns null when pending must
 * be discarded; returns the confirmation to store; `undefined` means leave
 * the previously owned confirmation unchanged (no pending).
 */
export function commitStagedPricingConfirmation(
  input: CommitInput
): PricingConfirmation | null | undefined {
  if (!input.pending) return undefined
  if (!input.networkIdle || !input.hasDisplayPayload) return undefined
  if (
    input.pending.epoch !== input.currentEpoch ||
    input.pending.queryKey !== input.currentQueryKey
  ) {
    return null
  }
  return input.pending
}

type RecoveryInput = {
  fetchEnabled: boolean
  /** Live fingerprint (pre-debounce). */
  liveEpoch: string
  /** Committed / debounced fingerprint — query key epoch. */
  committedEpoch: string
  networkInFlight: boolean
  /** Last Preview attempt failed (invariant: Failed). */
  confirmationRequestFailed: boolean
  pricingConfirmation: PricingConfirmation | null
}

/**
 * ADR Phase 4: idle + debounce-settled + no live-epoch confirmation + not failed
 * → schedule confirmation via existing refetch({ force }) (memo bypass only).
 *
 * Does not fire during debounce gaps (preserves quantity-edit scheduling).
 */
export function needsPricingConfirmationRecovery(input: RecoveryInput): boolean {
  if (!input.fetchEnabled) return false
  if (!input.committedEpoch) return false
  if (input.liveEpoch !== input.committedEpoch) return false
  if (input.networkInFlight) return false
  if (input.confirmationRequestFailed) return false
  if (
    input.pricingConfirmation != null &&
    input.pricingConfirmation.epoch === input.committedEpoch
  ) {
    return false
  }
  return true
}

export type ExternalPreviewHttpConfirmationInput = {
  /** True when the sync boundary is enabled (cart + auth ready). */
  fetchEnabled: boolean
  /** Live fingerprint (pre-debounce). */
  liveEpoch: string
  /** Committed / debounced fingerprint owned by the sync boundary. */
  committedEpoch: string
  /** Query key for the committed epoch. */
  committedQueryKey: string
  /**
   * Epoch the successful Continue (or equivalent) Preview HTTP was built for.
   * Must equal {@link committedEpoch} — never invent confirmation from a drifted live key.
   */
  previewEpoch: string
  /** Query key for that Preview (must equal {@link committedQueryKey}). */
  previewQueryKey: string
  payableAmount: number
  confirmedAt: number
}

/**
 * Promote a successful Preview HTTP that ran outside the sync fetcher
 * (e.g. Cart Continue) into a Pricing Confirmation via the same stage/commit helpers.
 *
 * Does not read transport memo or Redux — caller must supply payableAmount from the HTTP response
 * and epoch/queryKey identities that match the sync owner. Returns null when invariants fail.
 */
export function commitConfirmationFromSuccessfulPreviewHttp(
  input: ExternalPreviewHttpConfirmationInput
): PricingConfirmation | null {
  if (!input.fetchEnabled) return null
  if (!input.committedEpoch || !input.committedQueryKey) return null
  if (input.liveEpoch !== input.committedEpoch) return null
  if (input.previewEpoch !== input.committedEpoch) return null
  if (input.previewQueryKey !== input.committedQueryKey) return null

  const staged = stagePricingConfirmationAfterPreviewSuccess({
    epochAtStart: input.previewEpoch,
    queryKeyAtStart: input.previewQueryKey,
    currentEpoch: input.committedEpoch,
    currentQueryKey: input.committedQueryKey,
    confirmedAt: input.confirmedAt,
    payableAmount: input.payableAmount,
  })
  if (!staged) return null

  // External HTTP already completed; Display is seeded by the caller (memo + Redux).
  const committed = commitStagedPricingConfirmation({
    pending: staged,
    networkIdle: true,
    hasDisplayPayload: true,
    currentEpoch: input.committedEpoch,
    currentQueryKey: input.committedQueryKey,
  })
  return committed ?? null
}
